/**
 * Playbooks QA round 4, U10 (P1 + P2) / Decision 9 — the pure rules for a
 * play row: which kebab items it offers, what an unnamed play is called, and
 * what its delete dialog says.
 *
 * DOM-free on purpose, same reason as `config-page-logic.ts` /
 * `sequence-card-menu.ts` / `sequence-readiness.ts`: `bun:test` has no DOM
 * under `mini-apps/`, so anything that lives inside a Glaze-importing `.tsx`
 * file is untestable there. `components/plays-actions.tsx` renders whatever
 * this module says.
 *
 * Reuses `PlaybooksConfigRowMenuItemDescriptor` from `config-page-logic.ts`
 * rather than declaring a parallel shape: a kebab item is a kebab item, and
 * `KebabMenu` consumes the same `{action,label,danger?}` triple either way.
 * What it does NOT reuse is `getConfigRowMenuItems` itself — a play is not a
 * `PlaybooksConfigRowKind` (see {@link getPlayRowMenuItems}).
 */
import type { PlaybooksConfigRowMenuItemDescriptor } from './config-page-logic';
import type { PlaybooksPlaysLibraryGroup, PlaybooksPlaySummary } from './schemas';

/**
 * Rename + Delete, in that order, and nothing else.
 *
 * No `Edit`: a play's "edit" is opening its flow, which the row's own "Open"
 * link already is. No `Duplicate`: duplicating a play would mean a second
 * play on the same campaign+playbook, which "Create Play" already does
 * honestly and which a duplicate would make ambiguous.
 *
 * Takes NO `{ isAdmin }` option, deliberately unlike
 * `getConfigRowMenuItems`, whose playbook-row Delete IS Terra-admin-gated
 * (R8). Decision 7 is explicit that ANY authenticated Playbooks user may
 * delete a play: a play is campaign-scoped work product the rep themselves
 * created, not a shared library object other people's plays depend on. The
 * absent parameter is how that is encoded — there is no flag to pass wrongly.
 *
 * `label: 'Delete'` (not "Archive") even though the write is a SOFT archive,
 * matching the vocabulary Config already uses for its own soft-deletes: what
 * the rep is doing is making it go away, and the dialog is where the
 * "records are kept" nuance belongs.
 */
export function getPlayRowMenuItems(): PlaybooksConfigRowMenuItemDescriptor[] {
  return [
    { action: 'rename', label: 'Rename' },
    { action: 'delete', label: 'Delete', danger: true },
  ];
}

/**
 * What to call a play: its own `name`, else its playbook's name, else the raw
 * `playbookId`.
 *
 * The last rung is deliberately the ugly-but-true id rather than a generic
 * "Play": the playbook name is real data that is merely LATE (the library
 * query can still be in flight while the plays list renders), so inventing a
 * placeholder would be a hardcoded fallback for something that exists. The id
 * at least identifies the row, and it resolves to the real name a tick later.
 *
 * A whitespace-only `name` counts as no name — trimming here is what stops a
 * stray-space rename from rendering a blank row (the rename endpoint also
 * trims, so this is belt-and-suspenders for a value already persisted).
 */
export function playDisplayName(
  play: { name: string | null; playbookId: string },
  /**
   * A `ReadonlyMap`, matching what the real consumers already hold
   * (`PlaybooksHome`/`CalendarMonthGrid` build one `playbookNameById` Map and
   * pass it into `PlaysActions`) — taking a `Record` here would force every
   * call site to rebuild an object from that Map on each render for no gain.
   */
  playbookNameById: ReadonlyMap<string, string>,
): string {
  const named = play.name?.trim();
  if (named) return named;
  return playbookNameById.get(play.playbookId) ?? play.playbookId;
}

/** The delete dialog's title. Names the play so a mis-click is visible before it is confirmed. */
export function playDeleteTitle(displayName: string): string {
  return `Delete "${displayName}"?`;
}

/**
 * The delete dialog's body — ALWAYS warns (Decision 9), and warns harder when
 * the play has already sent.
 *
 * Two sentences are unconditional: what a delete actually does (the play
 * leaves this campaign's list) and, load-bearing, what it does NOT do
 * (segmentations / sequences / playbooks are untouched — a play delete never
 * cascades into the shared library). Without that second sentence a rep
 * deleting a play cannot tell it apart from deleting the playbook it uses.
 *
 * `sendCount > 0` appends the sent-records sentence. It deliberately does not
 * say "cannot" anywhere: Spencer's call is warn-don't-block, so a play with
 * sends is still deletable and the copy has to reflect that.
 *
 * `time(s)` rather than a pluralization branch: one string is one thing to
 * keep true, and the count is right next to it.
 */
export function playDeleteWarning(sendCount: number): string {
  const base =
    "It disappears from this campaign's plays. " +
    'Segments, sequences and playbooks it uses are not affected.';
  if (sendCount <= 0) return base;
  return `${base} This play has already sent ${sendCount} time(s); those records are kept.`;
}

/** `'launched'` is the only non-draft status a play can hold (`send.ts` flips it on a REAL send; test sends never do). Spencer's vocabulary for the Library is "draft or sent". */
export function playStatusLabel(status: string): 'Draft' | 'Sent' {
  return status === 'launched' ? 'Sent' : 'Draft';
}

export function playStatusBadgeStatus(status: string): 'success' | 'neutral' {
  return status === 'launched' ? 'success' : 'neutral';
}

/**
 * The Library search applied to the grouped plays read: a group survives when
 * its campaign name matches, or when at least one of its plays matches by
 * display name (`playDisplayName`, so an unnamed play matches on its
 * playbook's name) or by playbook name; in the play-match case only the
 * matching plays are kept. Empty/whitespace query returns the input untouched.
 */
export function filterPlaysLibrary(
  groups: readonly PlaybooksPlaysLibraryGroup[],
  query: string,
  playbookNameById: ReadonlyMap<string, string>,
): PlaybooksPlaysLibraryGroup[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...groups];
  const matches = (value: string | null) => value !== null && value.toLowerCase().includes(q);
  return groups.flatMap((group) => {
    if (matches(group.campaignName)) return [group];
    const plays = group.plays.filter(
      (play) =>
        matches(playDisplayName(play, playbookNameById)) ||
        matches(playbookNameById.get(play.playbookId) ?? null),
    );
    return plays.length === 0 ? [] : [{ ...group, plays }];
  });
}

/**
 * QA round 7, U1 (item 1) — `campaignSfdcId -> plays` from the ONE plays read
 * (`GET /plays/library`). Home's List view used to fire `GET /plays?campaignSfdcId`
 * per visible row; at real campaign counts that tripped Terra's OWN
 * `/api/tools` limiter (`src/middleware/index.ts#miniAppRateLimiter`, 300/min
 * per user per app in prod) — the 429s Spencer saw were ours, not Salesforce's.
 * Plays within a campaign are returned oldest-first, the order Home's rows
 * always had (`getPlays` ordered `created_at asc`; the library read is
 * newest-first for its own section), so the switch changes no row order.
 */
export function playsByCampaign(
  groups: readonly PlaybooksPlaysLibraryGroup[],
): Map<string, PlaybooksPlaySummary[]> {
  const map = new Map<string, PlaybooksPlaySummary[]>();
  for (const group of groups) {
    map.set(
      group.campaignSfdcId,
      [...group.plays].sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    );
  }
  return map;
}

/**
 * QA round 7, U1 (item 3) — the F7 merge, shared. The library's own map
 * first (it is what every other section renders from), then the plays read's
 * `playbookNames` for playbooks the library no longer lists (archived —
 * `GET /v2/library` filters `archived_at: null`, archiving a playbook does not
 * cascade to its plays, and `playDisplayName`'s fallback is the raw id).
 * Before this helper the merge lived inline in `PlaybooksConfigPage` and
 * Home / the Calendar / the play header each built a library-only map, so a
 * play of an archived playbook read as a UUID in three places.
 */
export function mergePlaybookNames(
  libraryPlaybooks: readonly { id: string; name: string }[] | undefined,
  playbookNames: Readonly<Record<string, string>> | undefined,
): Map<string, string> {
  const map = new Map((libraryPlaybooks ?? []).map((p) => [p.id, p.name] as const));
  for (const [id, name] of Object.entries(playbookNames ?? {})) {
    if (!map.has(id)) map.set(id, name);
  }
  return map;
}
